/*
 * Русский перевод настроек пользователя, оргструктуры (группы, пользователи) и уведомлений.
 */
import {
  createTranslationMessages,
  createTranslationResource,
} from '@backstage/core-plugin-api/alpha';
import { userSettingsTranslationRef } from '@backstage/plugin-user-settings';
import { orgTranslationRef } from '@backstage/plugin-org';
import { notificationsTranslationRef } from '@backstage/plugin-notifications';

const userSettingsRu = createTranslationMessages({
  ref: userSettingsTranslationRef,
  full: false,
  messages: {
    'languageToggle.title': 'Язык',
    'languageToggle.description': 'Язык интерфейса',
    'languageToggle.select': 'Выбрать язык {{language}}',
    'themeToggle.title': 'Тема',
    'themeToggle.description': 'Светлая или тёмная тема',
    'themeToggle.select': 'Выбрать {{theme}}',
    'themeToggle.selectAuto': 'Как в системе',
    'themeToggle.names.light': 'Светлая',
    'themeToggle.names.dark': 'Тёмная',
    'themeToggle.names.auto': 'Авто',
    'signOutMenu.title': 'Выйти',
    'signOutMenu.moreIconTitle': 'ещё',
    'pinToggle.title': 'Закрепить меню',
    'pinToggle.description': 'Не сворачивать боковое меню',
    'pinToggle.switchTitles.unpin': 'Открепить меню',
    'pinToggle.switchTitles.pin': 'Закрепить меню',
    'pinToggle.ariaLabelTitle': 'Переключатель закрепления меню',
    'identityCard.title': 'Учётная запись',
    'identityCard.noIdentityTitle': 'Нет учётной записи',
    'identityCard.userEntity': 'Сущность пользователя',
    'identityCard.ownershipEntities': 'Владение',
    'defaultProviderSettings.description':
      'Аутентификация в API и учётных записях {{provider}}',
    'emptyProviders.title': 'Нет провайдеров аутентификации',
    'emptyProviders.description':
      'Провайдеры аутентификации подключаются в конфигурации приложения.',
    'emptyProviders.action.title':
      'Откройте app-config.yaml и внесите изменения, как показано ниже:',
    'emptyProviders.action.readMoreButtonTitle': 'Подробнее',
    'providerSettingsItem.title.signIn': 'Войти в {{title}}',
    'providerSettingsItem.title.signOut': 'Выйти из {{title}}',
    'providerSettingsItem.buttonTitle.signIn': 'Войти',
    'providerSettingsItem.buttonTitle.signOut': 'Выйти',
    'authProviders.title': 'Провайдеры',
    'defaultSettingsPage.tabsTitle.general': 'Общие',
    'defaultSettingsPage.tabsTitle.authProviders': 'Аутентификация',
    'defaultSettingsPage.tabsTitle.featureFlags': 'Экспериментальные функции',
    'featureFlags.title': 'Экспериментальные функции',
    'featureFlags.description': 'После переключения обновите страницу',
    'featureFlags.emptyFlags.title': 'Экспериментальных функций нет',
    'featureFlags.emptyFlags.description':
      'Плагины могут регистрировать функции, которые пользователь включает сам — например, для ручного A/B-тестирования.',
    'featureFlags.emptyFlags.action.title':
      'Пример регистрации функции показан ниже:',
    'featureFlags.emptyFlags.action.readMoreButtonTitle': 'Подробнее',
    'featureFlags.filterTitle': 'Фильтр',
    'featureFlags.clearFilter': 'Сбросить фильтр',
    'featureFlags.flagItem.title.disable': 'Выключить',
    'featureFlags.flagItem.title.enable': 'Включить',
    'featureFlags.flagItem.subtitle.registeredInApplication':
      'Зарегистрирована в приложении',
    'featureFlags.flagItem.subtitle.registeredInPlugin':
      'Зарегистрирована в плагине {{pluginId}}',
    'settingsLayout.title': 'Настройки',
    sidebarTitle: 'Настройки',
    'profileCard.title': 'Профиль',
    'appearanceCard.title': 'Оформление',
  },
});

const orgRu = createTranslationMessages({
  ref: orgTranslationRef,
  full: false,
  messages: {
    'groupProfileCard.groupNotFound': 'Группа не найдена',
    'groupProfileCard.editIconButtonTitle': 'Редактировать метаданные',
    'groupProfileCard.refreshIconButtonTitle': 'Запланировать обновление',
    'groupProfileCard.refreshIconButtonAriaLabel': 'Обновить',
    'groupProfileCard.listItemTitle.entityRef': 'Ссылка на сущность',
    'groupProfileCard.listItemTitle.email': 'Почта',
    'groupProfileCard.listItemTitle.parentGroup': 'Родительская группа',
    'groupProfileCard.listItemTitle.childGroups': 'Дочерние группы',
    'membersListCard.cardLabel': 'Страница пользователя {{memberName}}',
    'membersListCard.title': 'Участники {{groupName}}',
    'membersListCard.noMembersDescription': 'В группе нет участников.',
    'membersListCard.noSearchResult':
      'Участники по запросу «{{searchTerm}}» не найдены.',
    'membersListCard.aggregateMembersToggle.label': 'Включая подгруппы',
    'ownershipCard.title': 'Владение',
    'ownershipCard.aggregateRelationsToggle.label':
      'Включая косвенное владение',
    'userProfileCard.userNotFound': 'Пользователь не найден',
    'userProfileCard.editIconButtonTitle': 'Редактировать метаданные',
    'userProfileCard.listItemTitle.email': 'Почта',
    'userProfileCard.listItemTitle.memberOf': 'Состоит в',
    'userProfileCard.moreGroupButtonTitle': '…ещё ({{number}})',
    'userProfileCard.allGroupDialog.title': 'Все группы {{name}}:',
    'userProfileCard.allGroupDialog.closeButtonTitle': 'Закрыть',
  },
});

const notificationPlurals: Record<string, string> = {
  'notificationsPage.tableTitle.all_few': 'Все уведомления ({{count}})',
  'notificationsPage.tableTitle.all_many': 'Все уведомления ({{count}})',
  'notificationsPage.tableTitle.saved_few':
    'Сохранённые уведомления ({{count}})',
  'notificationsPage.tableTitle.saved_many':
    'Сохранённые уведомления ({{count}})',
  'notificationsPage.tableTitle.unread_few':
    'Непрочитанные уведомления ({{count}})',
  'notificationsPage.tableTitle.unread_many':
    'Непрочитанные уведомления ({{count}})',
  'notificationsPage.tableTitle.read_few':
    'Прочитанные уведомления ({{count}})',
  'notificationsPage.tableTitle.read_many':
    'Прочитанные уведомления ({{count}})',
};

const notificationsRu = createTranslationMessages({
  ref: notificationsTranslationRef,
  full: false,
  messages: {
    ...notificationPlurals,
    'notificationsPage.title': 'Уведомления',
    'notificationsPage.tableTitle.all_one': 'Все уведомления ({{count}})',
    'notificationsPage.tableTitle.all_other': 'Все уведомления ({{count}})',
    'notificationsPage.tableTitle.saved_one':
      'Сохранённые уведомления ({{count}})',
    'notificationsPage.tableTitle.saved_other':
      'Сохранённые уведомления ({{count}})',
    'notificationsPage.tableTitle.unread_one':
      'Непрочитанные уведомления ({{count}})',
    'notificationsPage.tableTitle.unread_other':
      'Непрочитанные уведомления ({{count}})',
    'notificationsPage.tableTitle.read_one':
      'Прочитанные уведомления ({{count}})',
    'notificationsPage.tableTitle.read_other':
      'Прочитанные уведомления ({{count}})',
    'filters.title': 'Фильтры',
    'filters.view.label': 'Показать',
    'filters.view.unread': 'Непрочитанные',
    'filters.view.read': 'Прочитанные',
    'filters.view.saved': 'Сохранённые',
    'filters.view.all': 'Все',
    'filters.createdAfter.label': 'Отправлены',
    'filters.createdAfter.placeholder': 'Уведомления с',
    'filters.createdAfter.last24h': 'За сутки',
    'filters.createdAfter.lastWeek': 'За неделю',
    'filters.createdAfter.anyTime': 'За всё время',
    'filters.sortBy.label': 'Сортировка',
    'filters.sortBy.placeholder': 'Поле сортировки',
    'filters.sortBy.newest': 'Сначала новые',
    'filters.sortBy.oldest': 'Сначала старые',
    'filters.sortBy.topic': 'По теме',
    'filters.sortBy.origin': 'По источнику',
    'filters.severity.label': 'Важность от',
    'filters.severity.critical': 'Критично',
    'filters.severity.high': 'Высокая',
    'filters.severity.normal': 'Обычная',
    'filters.severity.low': 'Низкая',
    'filters.topic.label': 'Тема',
    'filters.topic.anyTopic': 'Любая тема',
    'table.emptyMessage': 'Уведомлений нет',
    'table.pagination.firstTooltip': 'Первая страница',
    'table.pagination.labelDisplayedRows': '{from}–{to} из {count}',
    'table.pagination.labelRowsSelect': 'строк',
    'table.pagination.lastTooltip': 'Последняя страница',
    'table.pagination.nextTooltip': 'Следующая страница',
    'table.pagination.previousTooltip': 'Предыдущая страница',
    'table.bulkActions.markAllRead': 'Отметить все прочитанными',
    'table.bulkActions.markSelectedAsRead': 'Отметить выбранные прочитанными',
    'table.bulkActions.returnSelectedAmongUnread':
      'Вернуть выбранные в непрочитанные',
    'table.bulkActions.saveSelectedForLater': 'Сохранить выбранные на потом',
    'table.bulkActions.undoSaveForSelected': 'Не сохранять выбранные',
    'table.confirmDialog.title': 'Вы уверены?',
    'table.confirmDialog.markAllReadDescription':
      'Все уведомления будут отмечены прочитанными.',
    'table.confirmDialog.markAllReadConfirmation': 'Отметить все',
    'table.confirmDialog.cancel': 'Отмена',
    'table.errors.markAllReadFailed':
      'Не удалось отметить все уведомления прочитанными',
    'sidebar.title': 'Уведомления',
    'sidebar.errors.markAsReadFailed':
      'Не удалось отметить уведомление прочитанным',
    'sidebar.errors.fetchNotificationFailed':
      'Не удалось загрузить уведомление',
    'settings.title': 'Настройки уведомлений',
    'settings.errorTitle': 'Не удалось загрузить настройки',
    'settings.noSettingsAvailable':
      'Настроек уведомлений пока нет, загляните позже',
    'settings.table.origin': 'Источник',
    'settings.table.topic': 'Тема',
  },
});

export const settingsTranslations = [
  createTranslationResource({
    ref: userSettingsTranslationRef,
    translations: { ru: () => Promise.resolve({ default: userSettingsRu }) },
  }),
  createTranslationResource({
    ref: orgTranslationRef,
    translations: { ru: () => Promise.resolve({ default: orgRu }) },
  }),
  createTranslationResource({
    ref: notificationsTranslationRef,
    translations: { ru: () => Promise.resolve({ default: notificationsRu }) },
  }),
];
